import { useForm, Controller } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { useAppSelector, useAppDispatch } from 'store/index';
import { selectCompanyId } from 'store/selectors/me/company';
import { updateMarketplaceOnboarding } from 'store/slices/me';
import useAlertQueue from 'hooks/useAlertQueue';
import { useMutation } from '@tanstack/react-query';
import { updateCompany } from 'utils/api/companies';
import Form from 'storybookConfig/stories/organisms/Form';
import Heading from 'storybookConfig/stories/molecules/Heading';
import Input from 'storybookConfig/stories/molecules/Input';
import Select from 'storybookConfig/stories/molecules/Select';
import UnitInput from 'storybookConfig/stories/molecules/Input/UnitInput';
import RadioButton from 'storybookConfig/stories/molecules/RadioButton';
import Checkbox from 'storybookConfig/stories/molecules/Checkbox';
import PrimaryButton from 'storybookConfig/stories/molecules/Button/PrimaryButton';
import SecondaryButton from 'storybookConfig/stories/molecules/Button/SecondaryButton';
import IconButton from 'storybookConfig/stories/molecules/Button/IconButton';
import StatusIcon from 'storybookConfig/stories/molecules/Icon/StatusIcon';
import Divider from 'storybookConfig/stories/molecules/Divider';
import SegmentedControl from 'storybookConfig/stories/molecules/Input/SegmentedControl';
import styled from '@emotion/styled';
import Flex from '@react-css/flex';
import TertiaryButton from 'storybookConfig/stories/molecules/Button/TertiaryButton';
import { type ShippingRate, FULFILLMENT_TIME_OPTIONS } from 'types/models/company';
import type { FormProps, MarketplaceFormData } from './types';
import useSegment from 'hooks/useSegment';
import { COUNTRIES } from 'utils/countries';
import type { ReactSelectOption } from 'types/general';

interface FormFields {
  revSplit?: number | null;
  shippingCountry?: string;
  shippingRates?: {
    us?: Partial<ShippingRate> | null;
    international?: Partial<ShippingRate> | null;
  };
  retailerAccess?: string;
  approvalThreshold?: number;
  fulfillmentTimes?: string;
  returnPolicyURL?: string;
  returnPolicyOption?: 'hasPolicy' | 'noPolicy';
  isInternationalShippingDisabled?: boolean;
}

const ShippingSection = styled.div`
  width: 100%;
`;

const ShippingGroup = styled(Flex)`
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
  width: 100%;
`;

const RateContainer = styled(Flex)`
  align-items: center;
  gap: 16px;
  width: 100%;
`;

const InternationalCheckboxContainer = styled.div`
  margin-bottom: 12px;
`;

const FreeShippingContainer = styled.div`
  position: relative;
  padding-top: 8px;
`;

const DeleteIconContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
`;

const SectionTitle = styled(Heading)`
  margin-bottom: 24px;
`;

// Reusable validation function for shipping method inputs
const validateShippingMethod = (rateValue: string | number | undefined, errorMessage: string) => {
  return (value: string | undefined) => {
    if (rateValue && rateValue !== '' && rateValue !== '0' && (!value || value.trim() === '')) {
      return errorMessage;
    }
    return true;
  };
};

const AdditionalInfoForm = ({
  onNext,
  onBack,
  marketplaceData,
  setMarketplaceData,
  setFocusedField,
}: FormProps) => {
  const companyId = useAppSelector(selectCompanyId);
  const dispatch = useAppDispatch();
  const { addErrorAlert, addSuccessAlert } = useAlertQueue();
  const analytics = useSegment();
  const { handleSubmit, register, watch, control, formState, reset, setValue } =
    useForm<FormFields>({
      mode: 'onChange',
      values: {
        returnPolicyOption: marketplaceData?.returnPolicyURL ? 'hasPolicy' : 'noPolicy',
        revSplit: marketplaceData?.revSplit === null ? 60 : marketplaceData?.revSplit,
        ...marketplaceData,
      },
    });

  useEffect(() => {
    reset(
      {
        returnPolicyOption: marketplaceData?.returnPolicyURL ? 'hasPolicy' : 'noPolicy',
        ...marketplaceData,
      },
      { keepErrors: false }
    );
  }, [marketplaceData, reset]);

  const selectedOption = watch('returnPolicyOption');
  const isInternationalDisabled = watch('isInternationalShippingDisabled');

  // Watch shipping rate values for real-time validation
  const usFlatRate = watch('shippingRates.us.flatRate');
  const usFreeShippingThreshold = watch('shippingRates.us.freeShippingThreshold');
  const internationalFlatRate = watch('shippingRates.international.flatRate');
  const internationalFreeShippingThreshold = watch(
    'shippingRates.international.freeShippingThreshold'
  );

  // Local state to manage international shipping visibility
  const [isInternationalShippingHidden, setIsInternationalShippingHidden] = useState(
    isInternationalDisabled || false
  );

  // Local state to manage free shipping visibility for US and International
  const [isUSFreeShippingEnabled, setIsUSFreeShippingEnabled] = useState(
    !!(
      marketplaceData?.shippingRates?.us?.freeShippingThreshold ||
      marketplaceData?.shippingRates?.us?.freeShippingMethod
    )
  );

  const [isInternationalFreeShippingEnabled, setIsInternationalFreeShippingEnabled] = useState(
    !!(
      marketplaceData?.shippingRates?.international?.freeShippingThreshold ||
      marketplaceData?.shippingRates?.international?.freeShippingMethod
    )
  );

  const updatingCompany = useMutation({
    mutationFn: (data: FormFields) => {
      const internationalRates = data.isInternationalShippingDisabled
        ? null
        : data.shippingRates?.international;

      const updatedData: MarketplaceFormData = {
        revSplit: Number(data.revSplit),
        shippingCountry: data.shippingCountry,
        shippingRates: {
          us: {
            flatRate: data.shippingRates?.us?.flatRate
              ? Number(data.shippingRates.us.flatRate)
              : (null as any),
            flatRateMethod: data.shippingRates?.us?.flatRateMethod || (null as any),
            freeShippingThreshold: data.shippingRates?.us?.freeShippingThreshold
              ? Number(data.shippingRates.us.freeShippingThreshold)
              : (null as any),
            freeShippingMethod: data.shippingRates?.us?.freeShippingMethod || (null as any),
          },
          international:
            internationalRates && internationalRates.flatRate
              ? {
                  flatRate: internationalRates.flatRate
                    ? Number(internationalRates.flatRate)
                    : (null as any),
                  flatRateMethod: internationalRates.flatRateMethod || (null as any),
                  freeShippingThreshold: internationalRates.freeShippingThreshold
                    ? Number(internationalRates.freeShippingThreshold)
                    : (null as any),
                  freeShippingMethod: internationalRates.freeShippingMethod || (null as any),
                }
              : null,
        },
        retailerAccess: data.retailerAccess,
        approvalThreshold:
          data.retailerAccess === 'HYBRID' ? Number(data.approvalThreshold) : undefined,
        fulfillmentTimes: data.fulfillmentTimes,
        returnPolicyURL: data.returnPolicyOption === 'hasPolicy' ? data.returnPolicyURL : '',
        onboarded: true,
      };

      setMarketplaceData(updatedData);
      return updateCompany(companyId ?? '', { marketplaceSettings: updatedData });
    },
    onSuccess: (data) => {
      analytics?.track('Supplier Onboarding Additional Info Completed', {
        revenue_split: data.marketplaceSettings.revSplit,
        flat_rate_shipping_domestic: data.marketplaceSettings.shippingRates?.us?.flatRate,
        free_shipping_domestic: data.marketplaceSettings.shippingRates?.us?.freeShippingThreshold,
        flat_rate_shipping_international:
          data.marketplaceSettings.shippingRates?.international?.flatRate,
        free_shipping_international:
          data.marketplaceSettings.shippingRates?.international?.freeShippingThreshold,
        shipping_rates: data.marketplaceSettings.shippingRates,
        retailer_access: data.marketplaceSettings.retailerAccess,
        annual_revenue_auto_approval_threshold: data.marketplaceSettings.approvalThreshold,
        average_fulfilment_time_days: data.marketplaceSettings.fulfillmentTimes,
        return_policy: data.marketplaceSettings.returnPolicyURL,
      });

      dispatch(updateMarketplaceOnboarding(data.marketplaceSettings));
      addSuccessAlert('Added additionl information', 'Your additional information has been saved.');
      if (onNext) {
        onNext();
      }
    },
    onError: (error: { message: string }) => {
      analytics?.track('Supplier Onboarding Step Error', {
        step_name: 'additional-info',
        error_message: error.message,
      });
      addErrorAlert('Something went wrong', error.message);
      console.error('Unable to update marketplace settings', error);
    },
  });

  return (
    <Form onSubmit={handleSubmit((data) => updatingCompany.mutate(data))}>
      <Flex>
        <TertiaryButton
          type="button"
          $removePadding="left"
          onClick={onBack}
          $iconName="navigate_before"
        >
          Back
        </TertiaryButton>
      </Flex>
      <Heading variant="Headings/H3">Additional Information</Heading>
      <Form.FieldGroup>
        <Form.Field
          labelText="Fulfillment Times"
          id="fulfillmentTimes"
          isRequired
          errorText={formState.errors.fulfillmentTimes?.message}
        >
          <Controller
            name="fulfillmentTimes"
            control={control}
            rules={{ required: 'Please select your fulfillment time' }}
            render={({ field }) => (
              <Select
                options={FULFILLMENT_TIME_OPTIONS}
                value={
                  FULFILLMENT_TIME_OPTIONS.find((option) => option.value === field.value) || null
                }
                onChange={(option) => {
                  field.onChange(option?.value);
                  setMarketplaceData({ fulfillmentTimes: option?.value });
                }}
                onFocus={() => setFocusedField?.('fulfillmentTimes')}
                placeholder="Select fulfillment time"
                isFullWidth
              />
            )}
          />
        </Form.Field>
      </Form.FieldGroup>
      <Divider />
      <Form.FieldGroup>
        <RadioButton
          value="hasPolicy"
          {...register('returnPolicyOption')}
          onFocus={() => setFocusedField?.('returnPolicy')}
        >
          Our return policy can be found here:
        </RadioButton>
        {selectedOption === 'hasPolicy' && (
          <Form.Field labelText="Return Policy URL" id="returnPolicyURL">
            <Input
              type="text"
              placeholder="https://www.example.com/return-policy"
              {...register('returnPolicyURL', {
                required: 'Return policy URL is required when you have a policy',
              })}
              onFocus={() => setFocusedField?.('returnPolicy')}
              isFullWidth
            />
          </Form.Field>
        )}
        <RadioButton
          value="noPolicy"
          {...register('returnPolicyOption')}
          onFocus={() => setFocusedField?.('returnPolicy')}
        >
          We do not accept returns
        </RadioButton>
        {selectedOption === 'noPolicy' && (
          <StatusIcon variant="warning">
            Having a return policy is recommended for a successful marketplace presence. It helps
            build trust with retailers and makes them more likely to partner with you.
          </StatusIcon>
        )}
      </Form.FieldGroup>
      <Divider />
      <Form.FieldGroup>
        <Controller
          name="revSplit"
          control={control}
          rules={{ required: 'Revenue split is required' }}
          render={({ field }) => {
            const handleNumberInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
              const val = Number(e.target.value);
              if (!Number.isNaN(val)) {
                const clamped = Math.min(Math.max(val, 0), 100);
                field.onChange(clamped);
                setMarketplaceData({ revSplit: clamped });
              }
            };

            return (
              <Form.Field
                labelText="Revenue Split"
                id="revSplitInput"
                errorText={formState.errors.revSplit?.message}
                isRequired
              >
                <UnitInput
                  value={field.value == null ? 60 : field.value}
                  onChange={handleNumberInputChange}
                  onFocus={() => setFocusedField?.('revSplit')}
                  min={0}
                  max={100}
                  unit="%"
                  step={1}
                  isFullWidth
                />
                {field.value != null && (field.value <= 5 || field.value >= 90) && (
                  <>
                    {field.value <= 5 && (
                      <StatusIcon variant="warning">
                        It looks like the retailer is taking a big chunk of your revenue, and that
                        might not be the best choice for you.
                      </StatusIcon>
                    )}
                    {field.value >= 90 && (
                      <StatusIcon variant="warning">
                        It seems like you&nbsp;re taking a significant portion of the revenue, which
                        might make it challenging for retailers to partner with you.
                      </StatusIcon>
                    )}
                  </>
                )}
              </Form.Field>
            );
          }}
        />
      </Form.FieldGroup>
      <Divider />
      <Form.FieldGroup>
        <Form.Field
          labelText="Retailer Access"
          id="retailerAccess"
          isRequired
          errorText={formState.errors.retailerAccess?.message}
        >
          <Controller
            name="retailerAccess"
            control={control}
            rules={{ required: 'Please select a retailer access option' }}
            render={({ field }) => (
              <SegmentedControl
                options={[
                  { label: 'Open', value: 'OPEN' },
                  { label: 'Hybrid', value: 'HYBRID' },
                  { label: 'Curated', value: 'CURATED' },
                ]}
                value={field.value}
                onChange={(value) => {
                  field.onChange(value);
                  if (value === 'HYBRID') {
                    setMarketplaceData({
                      retailerAccess: value,
                      approvalThreshold: 10000,
                    });
                    setValue('approvalThreshold', 10000);
                  } else {
                    setMarketplaceData({
                      retailerAccess: value,
                      approvalThreshold: undefined,
                    });
                    setValue('approvalThreshold', undefined);
                  }
                }}
                onFocus={() => setFocusedField?.('retailerAccess')}
              />
            )}
          />
        </Form.Field>
        {watch('retailerAccess') === 'HYBRID' && (
          <Form.Field
            labelText="Auto Approval Threshold"
            id="approvalThreshold"
            errorText={formState.errors.approvalThreshold?.message}
          >
            <Controller
              name="approvalThreshold"
              control={control}
              rules={{ required: 'Please enter an approval threshold' }}
              render={({ field }) => (
                <Input
                  type="number"
                  value={field.value}
                  onChange={(e) => {
                    field.onChange(Number(e.target.value));
                    setMarketplaceData({ approvalThreshold: Number(e.target.value) });
                  }}
                  onBlur={(e) => {
                    const value = Number(e.target.value);
                    let snappedValue = value;

                    // Snap to 1,000 if below minimum
                    if (value < 1000) {
                      snappedValue = 1000;
                    }
                    // Snap to 1,000,000 if above maximum
                    else if (value > 1000000) {
                      snappedValue = 1000000;
                    }

                    if (snappedValue !== value) {
                      field.onChange(snappedValue);
                      setMarketplaceData({ approvalThreshold: snappedValue });
                    }
                  }}
                  placeholder="Threshold amount"
                  step={0.1}
                  min={0}
                  onFocus={() => setFocusedField?.('retailerAccess')}
                  isFullWidth
                />
              )}
            />
          </Form.Field>
        )}
      </Form.FieldGroup>
      <Divider />
      <Form.FieldGroup>
        <Form.Field
          labelText="Shipping From"
          id="shippingCountry"
          isRequired
          errorText={formState.errors.shippingCountry?.message}
        >
          <Controller
            name="shippingCountry"
            control={control}
            rules={{ required: 'Please select a shipping country' }}
            render={({ field }) => (
              <Select
                options={
                  COUNTRIES.map((country) => ({
                    label: country.name,
                    value: country.code,
                  })) as ReactSelectOption[]
                }
                value={
                  COUNTRIES.find((country) => country.code === field.value)
                    ? {
                        label: COUNTRIES.find((country) => country.code === field.value)!.name,
                        value: field.value,
                      }
                    : null
                }
                onChange={(option) => {
                  field.onChange(option?.value);
                  setMarketplaceData({ shippingCountry: option?.value });
                }}
                onFocus={() => setFocusedField?.('shippingRates')}
                placeholder="Select shipping country"
                isFullWidth
              />
            )}
          />
        </Form.Field>
        <ShippingSection>
          <SectionTitle variant="Headings/H4">Domestic</SectionTitle>
          <ShippingGroup>
            <Form.Field
              id="usFlatRate"
              labelText="Flat Rate"
              errorText={
                formState.errors.shippingRates?.us?.flatRate?.message ||
                formState.errors.shippingRates?.us?.flatRateMethod?.message
              }
            >
              <RateContainer>
                <Input
                  type="number"
                  step="0.01"
                  {...register('shippingRates.us.flatRate', {
                    value: marketplaceData?.shippingRates?.us?.flatRate,
                    onChange: (e) => {
                      const { value } = e.target;
                      setValue('shippingRates.us.flatRate', value);
                      setMarketplaceData({
                        shippingRates: {
                          ...marketplaceData?.shippingRates,
                          us: { ...marketplaceData?.shippingRates?.us, flatRate: value }, // Keep as string
                        },
                      });
                    },
                  })}
                  placeholder="$0.00"
                  style={{ minWidth: '100px', maxWidth: '120px' }}
                  onFocus={() => setFocusedField?.('shippingRates')}
                />
                <Input
                  type="text"
                  {...register('shippingRates.us.flatRateMethod', {
                    validate: validateShippingMethod(
                      usFlatRate,
                      'Shipping method is required when flat rate is set'
                    ),
                    onChange: (e) => {
                      const { value } = e.target;
                      setValue('shippingRates.us.flatRateMethod', value);
                      setMarketplaceData({
                        shippingRates: {
                          ...marketplaceData?.shippingRates,
                          us: { ...marketplaceData?.shippingRates?.us, flatRateMethod: value },
                        },
                      });
                    },
                  })}
                  placeholder="Enter shipping rate type"
                  style={{ flex: 1 }}
                  onFocus={() => setFocusedField?.('shippingRates')}
                />
              </RateContainer>
            </Form.Field>

            {!isUSFreeShippingEnabled && (
              <SecondaryButton size="small" onClick={() => setIsUSFreeShippingEnabled(true)}>
                Add Free Shipping
              </SecondaryButton>
            )}

            {isUSFreeShippingEnabled && (
              <FreeShippingContainer>
                <Form.Field
                  id="usFreeShipping"
                  labelText="Free Shipping"
                  errorText={
                    formState.errors.shippingRates?.us?.freeShippingThreshold?.message ||
                    formState.errors.shippingRates?.us?.freeShippingMethod?.message
                  }
                >
                  <DeleteIconContainer>
                    <IconButton
                      size="small"
                      name="delete"
                      $iconName="delete"
                      onClick={() => {
                        setIsUSFreeShippingEnabled(false);
                        // Clear free shipping data
                        setValue('shippingRates.us.freeShippingThreshold', undefined);
                        setValue('shippingRates.us.freeShippingMethod', '');
                        setMarketplaceData({
                          shippingRates: {
                            ...marketplaceData?.shippingRates,
                            us: {
                              ...marketplaceData?.shippingRates?.us,
                              freeShippingThreshold: undefined,
                              freeShippingMethod: '',
                            },
                          },
                        });
                      }}
                    />
                  </DeleteIconContainer>
                  <RateContainer>
                    <Input
                      type="number"
                      step="0.01"
                      {...register('shippingRates.us.freeShippingThreshold', {
                        valueAsNumber: true,
                        onChange: (e) => {
                          const { value } = e.target;
                          setValue('shippingRates.us.freeShippingThreshold', value);
                          setMarketplaceData({
                            shippingRates: {
                              ...marketplaceData?.shippingRates,
                              us: {
                                ...marketplaceData?.shippingRates?.us,
                                freeShippingThreshold: value, // Keep as string
                              },
                            },
                          });
                        },
                      })}
                      placeholder="$0.00"
                      style={{ minWidth: '100px', maxWidth: '120px' }}
                      onFocus={() => setFocusedField?.('shippingRates')}
                    />
                    <Input
                      type="text"
                      {...register('shippingRates.us.freeShippingMethod', {
                        validate: validateShippingMethod(
                          usFreeShippingThreshold,
                          'Free shipping method is required when threshold is set'
                        ),
                        onChange: (e) => {
                          const { value } = e.target;
                          setValue('shippingRates.us.freeShippingMethod', value);
                          setMarketplaceData({
                            shippingRates: {
                              ...marketplaceData?.shippingRates,
                              us: {
                                ...marketplaceData?.shippingRates?.us,
                                freeShippingMethod: value,
                              },
                            },
                          });
                        },
                      })}
                      placeholder="Enter shipping rate type"
                      style={{ flex: 1 }}
                      onFocus={() => setFocusedField?.('shippingRates')}
                    />
                  </RateContainer>
                </Form.Field>
              </FreeShippingContainer>
            )}
          </ShippingGroup>

          <SectionTitle variant="Headings/H4">International</SectionTitle>

          <InternationalCheckboxContainer>
            <Checkbox
              name="isInternationalShippingDisabled"
              checked={isInternationalShippingHidden}
              onChange={(e) => {
                const isChecked = e.target.checked;
                setIsInternationalShippingHidden(isChecked);

                // Update form state
                setValue('isInternationalShippingDisabled', isChecked);

                // Update parent state
                setMarketplaceData({
                  shippingRates: {
                    ...marketplaceData?.shippingRates,
                    international: isChecked ? null : marketplaceData?.shippingRates?.international,
                  },
                });
              }}
              onFocus={() => setFocusedField?.('shippingRates')}
            >
              We don&apos;t offer International Shipping
            </Checkbox>
          </InternationalCheckboxContainer>

          {!isInternationalShippingHidden && (
            <ShippingGroup>
              <Form.Field
                id="internationalFlatRate"
                labelText="Flat Rate"
                errorText={
                  formState.errors.shippingRates?.international?.flatRate?.message ||
                  formState.errors.shippingRates?.international?.flatRateMethod?.message
                }
              >
                <RateContainer>
                  <Input
                    type="number"
                    step="0.01"
                    {...register('shippingRates.international.flatRate', {
                      valueAsNumber: true,
                      onChange: (e) => {
                        const { value } = e.target;
                        setValue('shippingRates.international.flatRate', value);
                        setMarketplaceData({
                          shippingRates: {
                            ...marketplaceData?.shippingRates,
                            international: {
                              ...marketplaceData?.shippingRates?.international,
                              flatRate: value, // Keep as string
                            },
                          },
                        });
                      },
                    })}
                    placeholder="$0.00"
                    style={{ minWidth: '100px', maxWidth: '120px' }}
                    onFocus={() => setFocusedField?.('shippingRates')}
                  />
                  <Input
                    type="text"
                    {...register('shippingRates.international.flatRateMethod', {
                      validate: validateShippingMethod(
                        internationalFlatRate,
                        'International shipping method is required when flat rate is set'
                      ),
                      onChange: (e) => {
                        const { value } = e.target;
                        setValue('shippingRates.international.flatRateMethod', value);
                        setMarketplaceData({
                          shippingRates: {
                            ...marketplaceData?.shippingRates,
                            international: {
                              ...marketplaceData?.shippingRates?.international,
                              flatRateMethod: value,
                            },
                          },
                        });
                      },
                    })}
                    placeholder="Enter shipping rate type"
                    style={{ flex: 1 }}
                    onFocus={() => setFocusedField?.('shippingRates')}
                  />
                </RateContainer>
              </Form.Field>

              {!isInternationalFreeShippingEnabled && (
                <SecondaryButton
                  size="small"
                  onClick={() => setIsInternationalFreeShippingEnabled(true)}
                >
                  Add Free Shipping
                </SecondaryButton>
              )}

              {isInternationalFreeShippingEnabled && (
                <FreeShippingContainer>
                  <Form.Field
                    id="internationalFreeShipping"
                    labelText="Free Shipping"
                    errorText={
                      formState.errors.shippingRates?.international?.freeShippingThreshold
                        ?.message ||
                      formState.errors.shippingRates?.international?.freeShippingMethod?.message
                    }
                  >
                    <DeleteIconContainer>
                      <IconButton
                        size="small"
                        name="delete"
                        $iconName="delete"
                        onClick={() => {
                          setIsInternationalFreeShippingEnabled(false);
                          // Clear free shipping data
                          setValue('shippingRates.international.freeShippingThreshold', undefined);
                          setValue('shippingRates.international.freeShippingMethod', '');
                          setMarketplaceData({
                            shippingRates: {
                              ...marketplaceData?.shippingRates,
                              international: {
                                ...marketplaceData?.shippingRates?.international,
                                freeShippingThreshold: undefined,
                                freeShippingMethod: '',
                              },
                            },
                          });
                        }}
                      />
                    </DeleteIconContainer>
                    <RateContainer>
                      <Input
                        type="number"
                        step="0.01"
                        {...register('shippingRates.international.freeShippingThreshold', {
                          valueAsNumber: true,
                          onChange: (e) => {
                            const { value } = e.target;
                            setValue('shippingRates.international.freeShippingThreshold', value);
                            setMarketplaceData({
                              shippingRates: {
                                ...marketplaceData?.shippingRates,
                                international: {
                                  ...marketplaceData?.shippingRates?.international,
                                  freeShippingThreshold: value, // Keep as string
                                },
                              },
                            });
                          },
                        })}
                        placeholder="$0.00"
                        style={{ minWidth: '100px', maxWidth: '120px' }}
                        onFocus={() => setFocusedField?.('shippingRates')}
                      />
                      <Input
                        type="text"
                        {...register('shippingRates.international.freeShippingMethod', {
                          validate: validateShippingMethod(
                            internationalFreeShippingThreshold,
                            'International free shipping method is required when threshold is set'
                          ),
                          onChange: (e) => {
                            const { value } = e.target;
                            setValue('shippingRates.international.freeShippingMethod', value);
                            setMarketplaceData({
                              shippingRates: {
                                ...marketplaceData?.shippingRates,
                                international: {
                                  ...marketplaceData?.shippingRates?.international,
                                  freeShippingMethod: value,
                                },
                              },
                            });
                          },
                        })}
                        placeholder="Enter shipping rate type"
                        style={{ flex: 1 }}
                        onFocus={() => setFocusedField?.('shippingRates')}
                      />
                    </RateContainer>
                  </Form.Field>
                </FreeShippingContainer>
              )}
            </ShippingGroup>
          )}
        </ShippingSection>
      </Form.FieldGroup>
      <Form.Footer column gap="16px">
        <PrimaryButton type="submit" disabled={updatingCompany.status === 'loading'}>
          {updatingCompany.status === 'loading' ? 'Saving...' : 'Next'}
        </PrimaryButton>
      </Form.Footer>
    </Form>
  );
};

export default AdditionalInfoForm;
